/**
 * Coder feature types (P5 design §9.1, §12) — read models for the coder
 * `coderApi` queries, the three wizard draft slices, and the job-status
 * vocabulary. Everything mirrors the exact backend shapes:
 * - daytona `GET /daytona/images` returns raw Mongoose docs (`_id`);
 * - repositories lists/PRs/coding-jobs/vaults unwrap their `{data}` envelopes
 *   in the API slice; `CodingJobView` is the service's plain mapped view
 *   (explicit `id`, ISO strings);
 * - vault surfaces expose NAMES AND KEYS ONLY — secret values never reach
 *   this app (design §12 hard guard; `POST /secrets/:id/read` is forbidden).
 */

/* ------------------------------------------------------------------ */
/* Read models                                                        */
/* ------------------------------------------------------------------ */

/** Mongoose-doc tolerant id pair: mapped views carry `id`, raw docs `_id`. */
export interface DocId {
  id?: string;
  _id?: string;
}

/** `GET /daytona/images` entry (`docker-image.schema.ts`, raw doc). */
export interface DockerImageView extends DocId {
  name: string;
  tag: string;
  image: string;
  description?: string;
  languages: string[];
  isDefault?: boolean;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

/** `GET /repositories` / `GET /repositories/:id` entry (raw doc). */
export interface RepositoryView extends DocId {
  name: string;
  gitUrl: string;
  provider?: 'github' | 'gitea';
  providerMeta?: { owner: string; repo: string; apiBase?: string };
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

/** `GET /repositories/:repoId/branches` entry (raw doc). */
export interface BranchView extends DocId {
  repositoryId?: string;
  name: string;
  gitBranchName: string;
  isDefault?: boolean;
  isActive?: boolean;
}

export type PullRequestWorkflowStatus = 'open' | 'reviewing' | 'merged' | 'closed';

/** `GET /repositories/:id/pull-requests` entry (raw doc). No URL field — the
 * link-out derives from the repository's `providerMeta` (design §10.2, A1). */
export interface PullRequestView extends DocId {
  repositoryId: string;
  provider: 'github' | 'gitea';
  providerNumber: number;
  title: string;
  headBranch: string;
  baseBranch: string;
  state?: 'open' | 'closed';
  status: PullRequestWorkflowStatus;
  agentConversationId?: string;
  createdAt?: string;
  updatedAt?: string;
}

export type CodingJobStatus = 'pending' | 'provisioned' | 'indexed' | 'pr-open' | 'failed' | 'done';

/** Terminal job statuses — §11.4 stops polling when all listed jobs are one of these. */
export const TERMINAL_CODING_JOB_STATUSES: readonly CodingJobStatus[] = ['done', 'failed'];

export function isTerminalJobStatus(status: CodingJobStatus): boolean {
  return TERMINAL_CODING_JOB_STATUSES.includes(status);
}

/** `coding-jobs.service.ts` board item — `sourceKey` is `<repositoryId>:<workBranch>`. */
export interface CodingJobItemView {
  repositoryId: string;
  branchId?: string;
  sandboxId?: string;
  prNumber?: number;
  status: CodingJobStatus;
  /** Optional defensively (Q4): derived client-side when the list omits it. */
  sourceKey?: string;
}

/** `GET /repositories/coding-jobs?projectId=` / `GET /:id` view (plain mapped object). */
export interface CodingJobView {
  id: string;
  projectId: string;
  factoryId: string;
  workBranch: string;
  taskDescription: string;
  baseBranch?: string;
  imageId?: string;
  status: CodingJobStatus;
  items: CodingJobItemView[];
  createdAt: string;
  updatedAt: string;
}

/** Metadata-only vault view (`vault.service.ts:32-40`) — never contains values. */
export interface VaultSummaryView {
  id: string;
  name: string;
  description?: string;
  projectId?: string;
  settings?: { allowAgentRead: boolean };
  createdAt?: string;
  updatedAt?: string;
}

/** Vault detail — entries are `{key, updatedAt}` only (design §8.2). */
export interface VaultDetailView extends VaultSummaryView {
  entries?: { key: string; updatedAt?: string }[];
}

/* ------------------------------------------------------------------ */
/* Wizard draft slices (design §9.1)                                  */
/* ------------------------------------------------------------------ */

/** `draft.steps['sandbox-image']` — the `select-image` slice. */
export interface SelectImageValue {
  imageId: string;
  name: string;
  tag: string;
  image: string;
  languages: string[];
  /** Display-ready one-liner ("node-dev:20") for generic renderers (Q2). */
  summary: string;
}

/** `draft.steps['attach-repository']` slice. */
export interface AttachRepositoryValue {
  repositoryId: string;
  repositoryName: string;
  provider?: 'github' | 'gitea';
  branchId: string;
  gitBranchName: string;
  /** Display-ready one-liner ("acme-api (GitHub) — base main"). */
  summary: string;
}

/** One vault reference — keys only, never values (P5-2). */
export interface SecretReference {
  vaultId: string;
  vaultName: string;
  keys: string[];
}

/** `draft.steps['attach-secrets']` slice. */
export interface AttachSecretsValue {
  references: SecretReference[];
  /** "3 keys across 2 vaults"; '' when nothing is attached. */
  summary: string;
}

/* ------------------------------------------------------------------ */
/* Draft-slice guards                                                 */
/* ------------------------------------------------------------------ */

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === 'string');
}

export function isSelectImageValue(value: unknown): value is SelectImageValue {
  if (!isRecord(value)) {
    return false;
  }
  return (
    typeof value.imageId === 'string' &&
    value.imageId !== '' &&
    typeof value.name === 'string' &&
    typeof value.tag === 'string' &&
    typeof value.image === 'string' &&
    typeof value.summary === 'string' &&
    isStringArray(value.languages)
  );
}

export function isAttachRepositoryValue(value: unknown): value is AttachRepositoryValue {
  if (!isRecord(value)) {
    return false;
  }
  return (
    typeof value.repositoryId === 'string' &&
    value.repositoryId !== '' &&
    typeof value.repositoryName === 'string' &&
    typeof value.branchId === 'string' &&
    typeof value.gitBranchName === 'string' &&
    typeof value.summary === 'string' &&
    (value.provider === undefined || value.provider === 'github' || value.provider === 'gitea')
  );
}

export function isAttachSecretsValue(value: unknown): value is AttachSecretsValue {
  if (!isRecord(value) || !Array.isArray(value.references)) {
    return false;
  }
  return (
    typeof value.summary === 'string' &&
    value.references.every(
      (reference) =>
        isRecord(reference) &&
        typeof reference.vaultId === 'string' &&
        typeof reference.vaultName === 'string' &&
        isStringArray(reference.keys),
    )
  );
}

/* ------------------------------------------------------------------ */
/* Factory coderConfig read view (P6-T3)                               */
/* ------------------------------------------------------------------ */

/**
 * Mirror of `FactoryCoderConfigCfg` (agent-core agent-factory.schema.ts) and
 * of `AgentCoderConfig` (agents/types.ts) — the coder wizard selections
 * that are NOW persisted on the factory (P6-T3: the wizard sends them as
 * create `overrides.coderConfig`; the from-template path stores them on the
 * factory doc and exposes them on `FactoryView.coderConfig`, surfaced here as
 * `AgentFactoryView.coderConfig`). Coder displays read THIS instead of
 * expecting empty values: `imageId` is the registered daytona image id (or
 * the template `select-image` options.defaultImage URI when the step was
 * untouched server-side), `repositoryId`/`branchName` the base-branch STARTING
 * POINT (jobs still fork their own `agent/<jobId>` branch, P5-T3), and
 * `secretsVaultId` a vault REFERENCE only — secret values never reach the
 * browser or the factory (P5-2 §12). `undefined` on factories created without
 * the coder wizard (backward-compatible; render the same "—" fallbacks as
 * today).
 */
export interface FactoryCoderConfig {
  imageId?: string;
  repositoryId?: string;
  branchName?: string;
  secretsVaultId?: string;
}
